import { Button, DialogContent, Box, Stack, Typography } from '@mui/material';
import { Warning, Error, Difference, FileUploadOutlined } from '@mui/icons-material';
import { MutableRefObject, ReactElement } from 'react';
import { dialogOverlay, StyledDialogActions, SuccessActionButton } from '../styles';
import { GenericModal } from '@/components/shared/GenericModal';
import { IdmsErrorTable } from '../IdmsErrorTable';
import { CsvParsedData, CsvValidationIssue } from '../types';
import { IdmsUploadsTypeEnum } from '@/__generated__/graphql';
import ProceedIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import { loadXlsxFromFile, removeRowsFromWorkbook, workbookToBlob } from '@/utils/xlsx';
import { generateFileDownload } from '@/utils/dom';

type Props = {
  open: boolean;
  handleClose: () => void;
  uploadedCsvParsedData?: CsvParsedData;
  idmsValidationErrors?: CsvValidationIssue;
  fileInputReference?: MutableRefObject<HTMLInputElement | null | undefined>;
  selectedFileType?: IdmsUploadsTypeEnum;
  idmsUploadLoading: boolean;
  file?: File | undefined;
  handleIdmsFileUpload: (file: File, validRecordsFile?: Blob) => void;
};

const exceptionIcons: { [key: string]: { icon: ReactElement; description: string } } = {
  Open: {
    icon: <Warning color="error" sx={{ fontSize: '1.6rem' }} />,
    description: 'Invalid values',
  },
  'N/A': {
    icon: <Difference color="warning" sx={{ fontSize: '1.6rem' }} />,
    description: 'Duplication error',
  },
  Closed: {
    icon: <Error sx={{ fontSize: '1.6rem', color: '#50739B' }} />,
    description: 'Missing value',
  },
};

const InvalidHeaderError = () => {
  return (
    <Typography variant="body1" sx={{ color: 'error.main', mt: 2 }}>
      The file header is invalid. Please upload a file with the correct header.
    </Typography>
  );
};

export const FileExceptionModal = ({
  open,
  handleClose,
  uploadedCsvParsedData,
  idmsValidationErrors,
  fileInputReference,
  selectedFileType,
  idmsUploadLoading,
  handleIdmsFileUpload,
  file,
}: Props) => {
  const handleReUpload = () => {
    if (fileInputReference) fileInputReference.current?.click();
    handleClose();
  };

  const ErrorDisplay = () => {
    if (!idmsValidationErrors || !uploadedCsvParsedData) {
      return <></>;
    }

    const downloadCsvWithInvalidRows = () => {
      const csvContent = `${uploadedCsvParsedData.header.join(',')}\n${uploadedCsvParsedData.rows
        .filter((_, index) => idmsValidationErrors.error.some((error) => error.line === index))
        .map((row) => Object.values(row).join(','))
        .join('\n')}`;

      generateFileDownload(csvContent, 'invalid-rows.csv');
    };

    switch (idmsValidationErrors.type) {
      case 'validation':
        return (
          <>
            <IdmsErrorTable
              errors={idmsValidationErrors.error}
              header={uploadedCsvParsedData.header}
              rows={uploadedCsvParsedData.rows}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={2} mt={2} data-testid="exception-icons-guide">
                {Object.values(exceptionIcons).map((exception) => (
                  <Stack
                    key={exception.description}
                    direction="row"
                    alignItems="center"
                    mt={2}
                    spacing={1}
                  >
                    {exception.icon}
                    <Typography fontSize="1.2rem" sx={{ color: 'text.secondary' }}>
                      {exception.description}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              {selectedFileType === IdmsUploadsTypeEnum.UpdateEquipments && (
                <SuccessActionButton
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadCsvWithInvalidRows}
                >
                  Download CSV with Invalid Rows
                </SuccessActionButton>
              )}
            </Stack>
          </>
        );

      case 'header':
        return <InvalidHeaderError />;
      default:
        return <p>An unexpected error occurred.</p>;
    }
  };

  const handleProceedWithValidRecords = async () => {
    if (!idmsValidationErrors || !uploadedCsvParsedData) return;

    const invalidLines = new Set(idmsValidationErrors.error.map((e) => e.line));

    // If the original file is an XLSX, remove invalid rows directly (preserves all formatting)
    if (file && /xlsx$/i.test(file.name)) {
      try {
        const workbook = await loadXlsxFromFile(file);
        removeRowsFromWorkbook(workbook, invalidLines);
        const blob = await workbookToBlob(workbook);
        handleIdmsFileUpload(file, blob);
      } catch (err) {
        console.error('Error rebuilding XLSX with valid rows:', err);
      }
      return;
    }

    const csvContent = `${uploadedCsvParsedData.header.join(',')}\n${uploadedCsvParsedData.rows
      .filter((_, index) => !invalidLines.has(index))
      .map((row) => Object.values(row).join(','))
      .join('\n')}`;

    const fileBlob = new Blob([csvContent], { type: 'text/csv' });
    if (!file) return;
    handleIdmsFileUpload(file, fileBlob);
  };

  return (
    <GenericModal
      open={open}
      handleClose={handleClose}
      title="The file contains an “exception”."
      description="Note: This file cannot be uploaded due to exceptions highlighted in the table below. Hover over each highlighted value to view the specific exception."
      crossIconStyle={{ backgroundColor: 'transparent', color: 'primary' }}
      sx={{ ...dialogOverlay, left: '15% !important', width: '70%', height: 700 }}
    >
      <DialogContent>
        <Box sx={{ maxHeight: 498, width: '100%', overflow: 'auto' }}>
          {idmsValidationErrors && uploadedCsvParsedData && <ErrorDisplay />}
        </Box>
      </DialogContent>
      <StyledDialogActions>
        <Button
          onClick={handleClose}
          disabled={idmsUploadLoading}
          variant="text"
          sx={{ mr: 1, height: '36px' }}
        >
          Cancel
        </Button>
        <SuccessActionButton
          variant="contained"
          startIcon={<FileUploadOutlined />}
          onClick={handleReUpload}
          disabled={idmsUploadLoading}
        >
          Re-Upload File
        </SuccessActionButton>
        {selectedFileType === IdmsUploadsTypeEnum.UpdateEquipments && (
          <SuccessActionButton
            variant="contained"
            startIcon={<ProceedIcon />}
            onClick={handleProceedWithValidRecords}
            disabled={idmsUploadLoading}
          >
            {idmsUploadLoading ? 'Uploading...' : 'Proceed with Valid Records'}
          </SuccessActionButton>
        )}
      </StyledDialogActions>
    </GenericModal>
  );
};
